import { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Tv2 } from 'lucide-react';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { ROUTES } from '../../api/routes';

type NivelPublicacao = 'MASTER' | 'ESTADO' | 'MUNICIPIO' | 'ESCOLA';
type DiaSemana = 'DOMINGO' | 'SEGUNDA' | 'TERCA' | 'QUARTA' | 'QUINTA' | 'SEXTA' | 'SABADO';

type Canal = {
  id: string;
  nome: string;
  slug: string;
  prioritario: boolean;
  tipoEntidade: string;
  entidadeId: string | null;
  _count?: { gradeSlots: number; publicacoes: number };
};

type Slot = {
  id?: string;
  diaSemana: DiaSemana;
  minutoInicio: number;
  minutoFim: number;
  nivelPermitido: NivelPublicacao;
};

const DIAS: DiaSemana[] = ['DOMINGO', 'SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'];
const NIVEIS: NivelPublicacao[] = ['MASTER', 'ESTADO', 'MUNICIPIO', 'ESCOLA'];

function toHour(minute: number) {
  const h = Math.floor(minute / 60).toString().padStart(2, '0');
  const m = (minute % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
}

export function TvGradeManagementPage() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [canais, setCanais] = useState<Canal[]>([]);
  const [canalId, setCanalId] = useState<string>('');
  const [slots, setSlots] = useState<Slot[]>([]);
  const [novoCanalNome, setNovoCanalNome] = useState('');
  const [feedback, setFeedback] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'grade' | 'conteudo' | 'indicadores'>('grade');
  const [publicacoes, setPublicacoes] = useState<Array<{ id: string; titulo: string; nivel: NivelPublicacao; canalId?: string | null; ativo: boolean }>>([]);
  const [form, setForm] = useState({ titulo: '', conteudoHtml: '', duracaoSegundos: 15 });

  const scopeLabel = useMemo(() => {
    switch (user?.perfil) {
      case 'ADMIN':
        return 'Master';
      case 'ESTADUAL':
        return 'Estadual';
      case 'MUNICIPAL':
        return 'Municipal';
      case 'ESCOLA':
        return 'Escola';
      default:
        return 'Gestão';
    }
  }, [user?.perfil]);

  useEffect(() => {
    void bootstrap();
  }, []);

  async function bootstrap() {
    setLoading(true);
    setFeedback('');
    try {
      await api.post(ROUTES.publicacoes.tv.ensurePrioritario);
      const { data } = await api.get<Canal[]>(ROUTES.publicacoes.tv.canais);
      setCanais(data);
      const first = data[0];
      if (first) {
        setCanalId(first.id);
        await loadGrade(first.id);
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadGrade(id: string) {
    const { data } = await api.get<{ canal: Canal; slots: Slot[] }>('/publicacoes/tv/grade', { params: { canalId: id } });
    setSlots(data.slots);
    await loadPublicacoes();
  }

  async function loadPublicacoes() {
    const { data } = await api.get<Array<{ id: string; titulo: string; nivel: NivelPublicacao; canalId?: string | null; ativo: boolean }>>(ROUTES.publicacoes.gestao);
    setPublicacoes(data);
  }

  async function onChangeCanal(id: string) {
    setCanalId(id);
    await loadGrade(id);
  }

  async function criarCanal() {
    if (!novoCanalNome.trim()) return;
    setSaving(true);
    setFeedback('');
    try {
      await api.post(ROUTES.publicacoes.tv.canais, { nome: novoCanalNome.trim() });
      setNovoCanalNome('');
      await bootstrap();
      setFeedback('Canal criado com sucesso.');
    } finally {
      setSaving(false);
    }
  }

  async function salvarGrade() {
    if (!canalId) return;
    setSaving(true);
    setFeedback('');
    try {
      await api.put(ROUTES.publicacoes.tv.grade(canalId), {
        slots: slots.map((slot) => ({
          diaSemana: slot.diaSemana,
          minutoInicio: slot.minutoInicio,
          minutoFim: slot.minutoFim,
          nivelPermitido: slot.nivelPermitido,
        })),
      });
      setFeedback('Grade salva com sucesso.');
      await loadGrade(canalId);
    } finally {
      setSaving(false);
    }
  }

  async function criarPublicacao() {
    if (!canalId || !form.titulo.trim()) return;
    setSaving(true);
    setFeedback('');
    try {
      await api.post(ROUTES.publicacoes.gestao, {
        titulo: form.titulo.trim(),
        conteudoHtml: form.conteudoHtml || '<p>Sem conteúdo adicional</p>',
        duracaoSegundos: form.duracaoSegundos,
        canalId,
      });
      setForm({ titulo: '', conteudoHtml: '', duracaoSegundos: 15 });
      await loadPublicacoes();
      setFeedback('Mídia associada ao canal com sucesso.');
    } finally {
      setSaving(false);
    }
  }

  const conflitInfo = useMemo(() => {
    const issues: string[] = [];
    for (const dia of DIAS) {
      const daySlots = slots.filter((s) => s.diaSemana === dia).sort((a, b) => a.minutoInicio - b.minutoInicio);
      if (daySlots.length === 0) {
        issues.push(`${dia}: sem slots configurados`);
        continue;
      }
      let cursor = 0;
      for (const slot of daySlots) {
        if (slot.minutoInicio !== cursor) {
          issues.push(`${dia}: buraco entre ${toHour(cursor)} e ${toHour(slot.minutoInicio)}`);
        }
        if (slot.minutoFim <= slot.minutoInicio) {
          issues.push(`${dia}: faixa inválida ${toHour(slot.minutoInicio)}-${toHour(slot.minutoFim)}`);
        }
        cursor = slot.minutoFim;
      }
      if (cursor !== 1440) {
        issues.push(`${dia}: grade não fecha 24h`);
      }
    }
    return issues;
  }, [slots]);

  const fallbackStats = useMemo(() => {
    const byNivel: Record<NivelPublicacao, number> = {
      MASTER: 0,
      ESTADO: 0,
      MUNICIPIO: 0,
      ESCOLA: 0,
    };
    for (const pub of publicacoes) {
      if (pub.ativo && pub.canalId === canalId) {
        byNivel[pub.nivel] += 1;
      }
    }
    const slotDemandByNivel: Record<NivelPublicacao, number> = {
      MASTER: 0,
      ESTADO: 0,
      MUNICIPIO: 0,
      ESCOLA: 0,
    };
    for (const slot of slots) {
      slotDemandByNivel[slot.nivelPermitido] += slot.minutoFim - slot.minutoInicio;
    }
    const estimate = (nivel: NivelPublicacao) =>
      slotDemandByNivel[nivel] > 0 && byNivel[nivel] === 0 ? 'ALTO' : byNivel[nivel] <= 1 ? 'MEDIO' : 'BAIXO';
    return { byNivel, slotDemandByNivel, estimate };
  }, [publicacoes, canalId, slots]);

  function updateNivel(dia: DiaSemana, index: number, nivel: NivelPublicacao) {
    const daySlots = slots.filter((s) => s.diaSemana === dia).sort((a, b) => a.minutoInicio - b.minutoInicio);
    if (!daySlots[index]) return;
    const target = daySlots[index];
    setSlots((prev) =>
      prev.map((slot) =>
        slot.diaSemana === target.diaSemana && slot.minutoInicio === target.minutoInicio && slot.minutoFim === target.minutoFim
          ? { ...slot, nivelPermitido: nivel }
          : slot,
      ),
    );
  }

  if (loading) {
    return <div style={{ padding: 24 }}>Carregando configuração de TV...</div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 1300, margin: '0 auto', display: 'grid', gap: 16 }}>
      <div style={{ background: '#0f172a', color: '#fff', borderRadius: 12, padding: 20, display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tv2 size={22} />
            Gestão de Grade TV - {scopeLabel}
          </h1>
          <p style={{ marginTop: 8, color: '#cbd5e1' }}>
            Cada esfera publica apenas na sua faixa. Quando uma faixa estiver vazia, o player usa conteúdo padrão do Master.
          </p>
        </div>
        <button onClick={salvarGrade} disabled={saving || !canalId} style={{ height: 42, padding: '0 16px', borderRadius: 8, border: 0, background: '#22c55e', color: '#052e16', fontWeight: 700, cursor: 'pointer' }}>
          <Save size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Salvar grade
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
        <select value={canalId} onChange={(e) => void onChangeCanal(e.target.value)} style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: 10 }}>
          {canais.map((c) => (
            <option key={c.id} value={c.id}>
              {c.prioritario ? '⭐ ' : ''}{c.nome} ({c._count?.publicacoes ?? 0} mídias)
            </option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={novoCanalNome} onChange={(e) => setNovoCanalNome(e.target.value)} placeholder="Nome do novo canal" style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: 10, width: 220 }} />
          <button onClick={criarCanal} disabled={saving} style={{ border: 0, borderRadius: 8, padding: '0 14px', background: '#2563eb', color: '#fff', fontWeight: 700 }}>
            <Plus size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Canal
          </button>
        </div>
      </div>

      {feedback && <div style={{ background: '#ecfeff', border: '1px solid #a5f3fc', color: '#155e75', borderRadius: 8, padding: 10 }}>{feedback}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => setActiveTab('grade')} style={{ borderRadius: 8, border: '1px solid #cbd5e1', padding: '8px 14px', background: activeTab === 'grade' ? '#dbeafe' : '#fff' }}>Grade semanal</button>
        <button onClick={() => setActiveTab('conteudo')} style={{ borderRadius: 8, border: '1px solid #cbd5e1', padding: '8px 14px', background: activeTab === 'conteudo' ? '#dbeafe' : '#fff' }}>Conteúdo por canal</button>
        <button onClick={() => setActiveTab('indicadores')} style={{ borderRadius: 8, border: '1px solid #cbd5e1', padding: '8px 14px', background: activeTab === 'indicadores' ? '#dbeafe' : '#fff' }}>Indicadores</button>
      </div>

      {activeTab === 'grade' && (
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ textAlign: 'left', padding: 12 }}>Dia</th>
              <th style={{ textAlign: 'left', padding: 12 }}>Faixas 24h</th>
            </tr>
          </thead>
          <tbody>
            {DIAS.map((dia) => {
              const daySlots = slots.filter((s) => s.diaSemana === dia).sort((a, b) => a.minutoInicio - b.minutoInicio);
              return (
                <tr key={dia} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: 12, width: 140, fontWeight: 700 }}>{dia}</td>
                  <td style={{ padding: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(180px, 1fr))', gap: 8 }}>
                      {daySlots.map((slot, idx) => (
                        <div key={`${dia}-${slot.minutoInicio}-${slot.minutoFim}`} style={{ border: '1px solid #dbeafe', borderRadius: 8, padding: 10, background: '#f8fbff' }}>
                          <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
                            {toHour(slot.minutoInicio)} - {toHour(slot.minutoFim)}
                          </div>
                          <select
                            value={slot.nivelPermitido}
                            onChange={(e) => updateNivel(dia, idx, e.target.value as NivelPublicacao)}
                            style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: 6, padding: 8 }}
                          >
                            {NIVEIS.map((nivel) => (
                              <option key={nivel} value={nivel}>
                                {nivel}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {activeTab === 'conteudo' && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, display: 'grid', gap: 12 }}>
          <h3 style={{ margin: 0 }}>Associar mídia ao canal</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 140px', gap: 8 }}>
            <input value={form.titulo} onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))} placeholder="Título da mídia" style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: 10 }} />
            <input
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={form.duracaoSegundos || ''}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '');
                setForm((f) => ({ ...f, duracaoSegundos: digits ? parseInt(digits, 10) : 0 }));
              }}
              placeholder="Duração (segundos)"
              style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: 10 }}
            />
            <button onClick={criarPublicacao} disabled={saving} style={{ border: 0, borderRadius: 8, background: '#16a34a', color: '#fff', fontWeight: 700 }}>Salvar mídia</button>
          </div>
          <textarea value={form.conteudoHtml} onChange={(e) => setForm((f) => ({ ...f, conteudoHtml: e.target.value }))} placeholder="Conteúdo (HTML simples)" style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: 10, minHeight: 100 }} />
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
            <strong>Mídias do canal selecionado</strong>
            <ul>
              {publicacoes.filter((p) => p.canalId === canalId && p.ativo).map((p) => (
                <li key={p.id}>{p.titulo} - nível {p.nivel}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'indicadores' && (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Validação visual de conflitos/ocupação</h3>
            {conflitInfo.length === 0 ? (
              <p style={{ color: '#166534' }}>Sem conflitos: grade cobre 24h dos 7 dias.</p>
            ) : (
              <ul>
                {conflitInfo.map((issue) => <li key={issue} style={{ color: '#b91c1c' }}>{issue}</li>)}
              </ul>
            )}
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Taxa estimada de fallback por esfera</h3>
            <p style={{ color: '#475569' }}>Estimativa baseada em slots alocados por nível e quantidade de mídias ativas no canal.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(140px, 1fr))', gap: 8 }}>
              {NIVEIS.map((nivel) => (
                <div key={nivel} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontWeight: 700 }}>{nivel}</div>
                  <div style={{ fontSize: 12 }}>Minutos alocados: {fallbackStats.slotDemandByNivel[nivel]}</div>
                  <div style={{ fontSize: 12 }}>Mídias: {fallbackStats.byNivel[nivel]}</div>
                  <div style={{ marginTop: 6, fontWeight: 700 }}>Risco fallback: {fallbackStats.estimate(nivel)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
